import { useContext, useEffect, useState } from 'react'
import { LayoutGrid, MessagesSquare, Undo2 } from 'lucide-react'
import { useLocation } from 'react-router-dom'
import {
  AuthContext, isChannelAgent, isPlatformUser, isTenantAdmin, resolveShellIdentity, type AdminUser,
} from '@retink/web-auth'
import { Button, ShellLayout, toast, useResource, type AppEntry, type NavEntry } from '@retink/web-ui'
import { managementItems, managementLabel, managementRouteScope } from 'admin/management'
import { api, DESK_URL, signOut, switchTenant } from '../lib/api'
import type { TenantContext, TenantOutletContext } from '../lib/tenant-context'
import { CONTROL_PRODUCTS, CONTROL_SERVICES, productForPath } from './products'

const applicationNav: NavEntry[] = [
  { to: '/dashboard', icon: LayoutGrid, label: 'Overview' },
  { to: '/conversations', icon: MessagesSquare, label: 'Conversations' },
]

interface ManagementScopeRecord {
  kind: 'organization' | 'tenant'
  name: string
  orgId?: string
  orgName?: string
}

export function AppShell() {
  const { user } = useContext(AuthContext)
  const deskHome = user && isChannelAgent(user) ? `${DESK_URL.replace(/\/+$/, '')}/` : null

  useEffect(() => {
    if (deskHome) window.location.replace(deskHome)
  }, [deskHome])

  if (!user) return null
  if (deskHome) {
    return <div className="flex h-full items-center justify-center bg-background text-sm text-muted-foreground">Taking you to the operator desk…</div>
  }
  return <ControlPlaneShell user={user} />
}

function ControlPlaneShell({ user }: { user: AdminUser }) {
  const { pathname } = useLocation()
  const { bootstrap, refresh } = useContext(AuthContext)
  const [exitingTenant, setExitingTenant] = useState(false)
  const tenantContext = bootstrap as TenantContext | null | undefined
  const tenant = tenantContext?.tenant ?? null
  const product = productForPath(pathname)
  const administration = pathname === '/admin' || pathname.startsWith('/admin/')
  const managementRoute = managementRouteScope(pathname)
  const { data: managementRecord } = useResource<ManagementScopeRecord | null>(
    () => managementRoute.tenantId
      ? api.get<{ name: string; org_id: string; org_name: string }>(
          `/api/admin/tenants/${managementRoute.tenantId}`,
        ).then((record) => ({
          kind: 'tenant' as const,
          name: record.name,
          orgId: record.org_id,
          orgName: record.org_name,
        }))
      : managementRoute.organizationId
        ? api.get<{ name: string }>(`/api/orgs/${managementRoute.organizationId}`)
            .then((record) => ({ kind: 'organization' as const, name: record.name }))
        : Promise.resolve(null),
    [managementRoute.tenantId ?? '', managementRoute.organizationId ?? ''],
  )
  const adminNav = managementItems(user, pathname, { tenantOrgId: managementRecord?.orgId })
  const inheritedTenantAccess = isPlatformUser(user) || user.orgRole === 'admin'
  const { data: tenantChoices = [] } = useResource<{ id: string }[]>(
    () => inheritedTenantAccess ? Promise.resolve([]) : api.get('/api/me/tenants'),
    [user.id, inheritedTenantAccess],
  )
  const activeTenant = tenant
    ? { name: tenant.name, orgName: tenant.org_name }
    : user.tenantId
      ? { name: user.tenantName ?? 'Current tenant' }
      : null
  const scope = resolveShellIdentity(user, activeTenant, tenantChoices.length > 1)
  const visibleAccent = administration && managementRecord
    ? managementRecord.kind === 'tenant' ? 'tenant' : 'org'
    : scope.accent
  const visibleNavGroup = administration && managementRecord
    ? {
        label: managementRecord.kind === 'tenant' ? 'Tenant' : 'Organization',
        entity: managementRecord.name,
      }
    : scope.navGroup
  // The pill states session identity (authority + acting scope) and nothing
  // else — the topbar already names the record being viewed, and a pill that
  // echoed the record made "Exit tenant" read as belonging to that record.

  const apps: AppEntry[] = (tenantContext?.products ?? [])
    .flatMap(({ productId, displayName }) => {
      const configured = CONTROL_PRODUCTS[productId]
      return configured ? [{
        id: productId,
        label: displayName || configured.label,
        href: configured.path,
        iconKey: configured.iconKey,
      }] : []
    })

  // Platform services are not tenant-launchable products — every tenant has
  // them, so the list is static (config surfaces, not workspaces). They are
  // still tenant-scoped: outside an active tenant (org/platform management)
  // the rail shows no apps and no services — enter a tenant to work.
  const services: AppEntry[] = user.tenantId
    ? Object.values(CONTROL_SERVICES).map((s) => ({
        id: s.id, label: s.label, href: s.nav.find((n) => 'to' in n)?.to ?? s.path, iconKey: s.iconKey,
      }))
    : []

  const tenantError = user.tenantId && bootstrap === undefined
    ? 'Tenant details unavailable'
    : null
  const outletContext: TenantOutletContext = {
    tenantContext: tenantContext ?? null,
    tenantLoading: false,
    tenantError,
    reloadTenant: refresh,
  }

  // /admin/tenant is the acting tenant's own settings — a tenant surface even
  // though no fleet management record backs it: tenant tag, tenant rail item,
  // and the SAME workbench chrome as every other tenant page (kev 2026-08-15:
  // it must not read as a different surface). Console chrome is for org and
  // platform management only.
  const inCurrentTenantSettings = pathname === '/admin/tenant' || pathname.startsWith('/admin/tenant/')
  const tag = administration
    ? managementRecord?.kind === 'tenant' || inCurrentTenantSettings ? 'Tenant' : managementRecord?.kind === 'organization' ? 'Organization' : 'Platform'
    : product?.label ?? 'Workspace'
  const nav = administration ? adminNav : product?.nav ?? applicationNav
  const workspacePage = applicationNav.find(
    (item) => 'to' in item && (pathname === item.to || pathname.startsWith(`${item.to}/`)),
  )
  const workspaceTitle = workspacePage && 'label' in workspacePage ? workspacePage.label : 'Overview'
  // Org and tenant records often share a name ("Getronics AI" the org vs the
  // tenant) and identical menu shapes — the top bar states which record kind
  // this page belongs to, so the two scopes never read as the same surface.
  const managementTitle = managementLabel(pathname, adminNav) ?? 'Platform'
  const topbar = administration
    ? {
        icon: LayoutGrid,
        title: managementRecord ? (
          <>
            <span className="text-muted-foreground">
              {managementRecord.kind === 'tenant' ? 'Tenant' : 'Organization'}{' '}
            </span>
            {managementRecord.name}
            <span className="text-muted-foreground"> · {managementTitle}</span>
          </>
        ) : inCurrentTenantSettings && activeTenant ? (
          // The acting tenant's own settings have no management record behind
          // them, but the topbar still names the tenant like any record page.
          <>
            <span className="text-muted-foreground">Tenant </span>
            {activeTenant.name}
            <span className="text-muted-foreground"> · {managementTitle}</span>
          </>
        ) : managementTitle,
      }
    : { icon: product?.icon ?? LayoutGrid, title: product?.label ?? workspaceTitle }

  async function exitTenant() {
    if (!scope.exitPath || exitingTenant) return
    setExitingTenant(true)
    try {
      await switchTenant(null, scope.exitPath)
    } catch {
      setExitingTenant(false)
      toast.error('Could not exit tenant')
    }
  }

  return (
    <ShellLayout
      tag={tag}
      accent={visibleAccent}
      chrome={administration && !inCurrentTenantSettings ? 'console' : 'workbench'}
      nav={nav}
      navGroup={visibleNavGroup}
      onSignOut={signOut}
      roleLabel={scope.authority}
      accountPath="/account"
      switchTenantPath={scope.canChooseTenant ? '/choose-tenant' : undefined}
      apps={apps}
      services={services}
      clientNavigation
      // 'admin' lights the rail's "Tenant settings" item — only true on the
      // acting tenant's settings, not on org/platform management pages.
      currentSurface={administration ? (inCurrentTenantSettings ? 'admin' : 'management') : product?.id ?? 'app'}
      homeHref={user.tenantId ? '/dashboard' : undefined}
      homeLabel="Overview"
      adminHref={user.tenantId && isTenantAdmin(user) ? '/admin/tenant' : undefined}
      adminLabel="Tenant settings"
      topbar={topbar}
      scopeIdentity={{ authority: scope.authority, location: scope.location }}
      actions={scope.exitPath ? (
        <Button type="button" variant="outline" size="sm" onClick={() => void exitTenant()} disabled={exitingTenant}>
          <Undo2 aria-hidden />
          {exitingTenant ? 'Exiting…' : 'Exit tenant'}
        </Button>
      ) : undefined}
      status={{
        left: tenantError
          ? <button type="button" onClick={refresh} className="text-destructive hover:underline">Tenant details unavailable — Retry</button>
          : tenant ? <span className="truncate">{tenant.slug} · {tenant.region}</span> : null,
        right: import.meta.env.PROD ? null : <span className="uppercase tracking-wider opacity-70">dev</span>,
      }}
      outletContext={outletContext}
    />
  )
}
